import React, { FC, useEffect, useRef, useState } from 'react';
import { documentService } from '../../utils/window.utils.js';
import { TFormPageNavigationProps } from './types/formPageNavigation.types';
import { FormPageNavigationModel } from './models/formPageNavigation.model';
import UnreleasedComponent from '../@shared/unreleasedComponent';
import Heading from '../@shared/heading';
import Step from './components/step';
import { debounceHandler } from '../../utils/debounceHandler.utils';
import useMediaQuery from '../../lib/custom-hooks/useMediaQuery';
import breakpoints from '../../constants/breakpoints.constants';
import DictionaryTerm from '../@shared/dictionaryTerm';
import { formPageIdentifierClass, sitecoreFormIdentifier } from '../../constants/multiStep.constants';
import './styles.scss';

const FormPageNavigation: FC<TFormPageNavigationProps> = (props: TFormPageNavigationProps) => {
    const model = new FormPageNavigationModel(props);
    const stepsCount = model.steps.length;
    const stepListRef = useRef<HTMLUListElement>(null);
    const stepRef = useRef<HTMLLIElement[]>([]);
    const timmerRef = useRef<ReturnType<typeof setTimeout>>();
    const [stepKey, updateStepKey] = useState<string>('');
    const stepKeyRef = useRef(stepKey);
    const [activeStepNumber, updateActiveStepNumber] = useState<number>(1);
    const [formSubmitted, updateFormSubmitted] = useState<boolean>(false);
    const progressBarWidthRef = useRef(0);
    const isDesktop = useMediaQuery(`(min-width: ${breakpoints['tbl-p']}px)`);
    const displayMobileStepInfo = !isDesktop && !formSubmitted;

    const setProgressBarStepWidth = () => {
        const stepListWidth = stepListRef?.current?.clientWidth || 0;
        const firstStepWidth = (stepRef?.current?.[0]?.clientWidth || 0) / 2;
        const lastStepWidth = (stepRef?.current?.[stepsCount - 1]?.clientWidth || 0) / 2;
        const stepProgressBarWidth = (stepListWidth - (firstStepWidth + lastStepWidth));
        documentService().documentElement?.style?.setProperty('--form-progress-bar-step-width', `${stepProgressBarWidth}px`);
        documentService().documentElement?.style?.setProperty('--form-progress-bar-first-step-width', `${firstStepWidth}px`);
        progressBarWidthRef.current = stepProgressBarWidth;
    }

    const calculateProgress = () => {
        let distance = 0;
        if (activeStepNumber > 1) {
            const activeStepCenter = stepRef.current[activeStepNumber - 1].offsetLeft + (stepRef.current[activeStepNumber - 1].offsetWidth) / 2;
            const firstStepCenter = stepRef.current[0].offsetLeft + (stepRef.current[0].offsetWidth)/2;
            distance = activeStepCenter - firstStepCenter;
        }
        return `${distance}px`;
    }

    const updateStepInfo = (key: string) => {
        updateStepKey(key);
    };

    useEffect(() => {
        stepKeyRef.current = stepKey;
    }, [stepKey]);

    useEffect(() => {
        setProgressBarStepWidth();
        const formIdentifierElement = documentService()?.querySelector(`.${formPageIdentifierClass}`);
        const currentStepKey = formIdentifierElement?.value;
        currentStepKey && updateStepKey(currentStepKey);
        const observer = new ResizeObserver((_) => {
            debounceHandler(() => setProgressBarStepWidth(), timmerRef, 10);
        });

        if (stepListRef?.current) {
            observer.observe(stepListRef.current);
        }

        const mutationCallback = () => {
            const formIdentifierElement = documentService()?.querySelector(`.${formPageIdentifierClass}`);
            const currentStepKey = formIdentifierElement.value;
            if (stepKeyRef.current !== currentStepKey) {
                updateStepInfo(currentStepKey);
            }
        };

        const mutationObserver = new MutationObserver(mutationCallback);
        setTimeout(() => {
            const formEle = documentService()?.querySelector(sitecoreFormIdentifier);
            const config = { attributes: true, childList: true, subtree: true };
            if (formEle) {
                mutationObserver.observe(formEle, config);
            } else {
                updateFormSubmitted(true);
            }
        }, 100);


        return () => {
            const formEle = documentService()?.querySelector(sitecoreFormIdentifier);
            stepListRef.current && observer.unobserve(stepListRef.current);
            formEle && mutationObserver.disconnect();
        };
    }, []);

    // Component is unreleased
    if (model.isUnreleased) {
        return <UnreleasedComponent />;
    }

    if (!model.hasSteps) {
        return null;
    }

    return (
        <div className="form-page-navigation__wrapper">
            <Heading
                title={model?.title}
                subtitle={model?.subtitle}
                eyebrow={model?.eyebrow}
            />
            <div className="form-page-navigation">
                <div className='form-page-navigation__progress-bar'>
                    <div className='form-page-navigation__progress' style={{ width: `${formSubmitted ? '100%' : calculateProgress()}` }}></div>
                </div>

                <ul className="form-page-navigation__step-list" ref={stepListRef}>
                    {model.steps.map((step, index) => {

                        return (
                            <Step
                                activeStepkey={stepKey}
                                stepKey={step.Key}
                                stepRef={stepRef}
                                stepNumber={index + 1}
                                stepText={step.text}
                                activeStepNumber={activeStepNumber}
                                updateActiveStepNumber={updateActiveStepNumber}
                                formSubmitted={formSubmitted}
                            />)
                    })}
                </ul>

                {
                    displayMobileStepInfo && (
                        <div className='form-page-navigation__step--mobile'>
                            <span className={'form-page-navigation__step__title'}><DictionaryTerm keyword={'cnhi-form-step'} /> {activeStepNumber}</span>
                            <span className={'form-page-navigation__step__name'}>{model.steps[activeStepNumber - 1].text}</span>
                        </div>
                    )
                }
            </div>
        </div>
    );
}

export default FormPageNavigation;